import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { BookOpen, GraduationCap, TrendingUp, Briefcase, CheckCircle, MessageCircle } from 'lucide-react';
export function ServicesPage() {
  const services = [{
    id: 'review',
    title: 'Credit Profile Analysis & Education',
    icon: BookOpen,
    description: 'An educational, line-by-line walkthrough of your credit profile so you understand the factors that shape it.',
    included: ['Educational report walkthrough', 'Explanation of scoring factors', 'Understanding utilization ratios', 'Clarifying what each section means'],
    idealFor: 'Individuals who want to understand their credit profile and how it is calculated.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=600&fit=crop'
  }, {
    id: 'education',
    title: 'Credit Education & Planning',
    icon: GraduationCap,
    description: 'Comprehensive education on how credit works, tailored to your specific situation and goals.',
    included: ['Understanding scoring models (FICO vs Vantage)', 'Principles of an optimal credit mix', 'Payment history best practices', 'Long-term educational roadmap'],
    idealFor: 'Anyone looking to learn how to build or rebuild credit responsibly through education.',
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&h=600&fit=crop'
  }, {
    id: 'habits',
    title: 'Financial Habits & Utilization Strategy',
    icon: TrendingUp,
    description: 'Educational consulting focused on the financial habits and utilization strategies that influence your profile.',
    included: ['Budgeting principles for debt reduction', 'Credit utilization strategy education', 'Savings automation guidance', 'Understanding debt-to-income ratios'],
    idealFor: 'Those who want to learn strong financial habits and utilization strategies.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'
  }, {
    id: 'business',
    title: 'Business Credit Readiness',
    icon: Briefcase,
    description: 'Educational guidance for entrepreneurs who want to understand how business credit works, separate from personal finances.',
    included: ['Entity structure basics', 'Understanding vendor trade lines', 'Business credit tier education', 'Separation of personal/business finances'],
    idealFor: 'Small business owners and aspiring entrepreneurs.',
    image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=600&fit=crop'
  }];
  return <Layout data-id="element-664">
      <div className="bg-slate-50 py-16 border-b border-slate-200" data-id="element-665">
        <Container data-id="element-666">
          <div className="max-w-3xl" data-id="element-667">
            <h1 className="text-4xl font-bold text-slate-900 mb-6" data-id="element-668">
              Our Services
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed" data-id="element-669">
              We offer professional education and strategic consulting to help
              you understand the world of credit and finance. Our approach is
              transparent and compliant — we teach and advise, we never act on
              your accounts for you.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-20" data-id="element-670">
        <div className="space-y-20" data-id="element-671">
          {services.map((service, index) => <div key={service.id} className={`flex flex-col md:flex-row gap-12 items-start ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-672">
              <div className="flex-1" data-id="element-673">
                <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-xl mb-6" data-id="element-674">
                  <service.icon className="h-8 w-8 text-blue-600" data-id="element-675" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-676">
                  {service.title}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-677">
                  {service.description}
                </p>

                <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 mb-8" data-id="element-678">
                  <h3 className="font-semibold text-slate-900 mb-4" data-id="element-679">
                    What You'll Learn:
                  </h3>
                  <ul className="space-y-3" data-id="element-680">
                    {service.included.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-681">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-682" />
                        <span className="text-slate-700" data-id="element-683">{item}</span>
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-684">
                  <h3 className="font-semibold text-slate-900 mb-2" data-id="element-685">
                    Ideal For:
                  </h3>
                  <p className="text-slate-600 mb-8" data-id="element-686">{service.idealFor}</p>
                </div>

                <a href="https://wa.me/17863763985" target="_blank" rel="noopener noreferrer" data-id="element-687">
                  <Button size="lg" data-id="element-688">
                    <MessageCircle className="h-5 w-5 mr-2" data-id="element-689" />
                    Contact Us
                  </Button>
                </a>
              </div>

              <div className="flex-1 w-full" data-id="element-690">
                <div className="relative h-full min-h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-691">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-692" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" data-id="element-693"></div>
                </div>
              </div>
            </div>)}
        </div>
      </Container>

      <section className="bg-slate-900 text-white py-16" data-id="element-694">
        <Container className="text-center" data-id="element-695">
          <h2 className="text-2xl font-bold mb-4" data-id="element-696">
            Not sure which service is right for you?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto" data-id="element-697">
            Call us for a general conversation and we'll help you understand the
            best educational path forward based on your situation.
          </p>
          <a href="https://wa.me/17863763985" target="_blank" rel="noopener noreferrer" data-id="element-698">
            <Button variant="primary" className="bg-white text-slate-900 hover:bg-slate-100" data-id="element-699">
              <MessageCircle className="h-5 w-5 mr-2" data-id="element-700" />
              Contact Us
            </Button>
          </a>
        </Container>
      </section>
    </Layout>;
}